// Draw horizontal slices of the current window so its contents bend with the
// funnel, rather than merely clipping a rectangular scale animation.
export function animateWindowToDock(
  element: HTMLElement, dock: HTMLElement, restoring: boolean, onFinish: () => void,
) {
  const rect = element.getBoundingClientRect();
  const target = dock.getBoundingClientRect();
  const overlay = document.createElement("div");
  overlay.className = "genie-visual";
  overlay.setAttribute("aria-hidden", "true");
  overlay.setAttribute("inert", "");
  const sourceNodes = [element, ...Array.from(element.querySelectorAll<HTMLElement>("*"))];
  const scrolls = sourceNodes.map((node, index) => ({ index, top: node.scrollTop, left: node.scrollLeft }))
    .filter(node => node.top || node.left);
  const count = 40;
  const sliceHeight = rect.height / count;
  const strips: HTMLDivElement[] = [];
  for (let index = 0; index < count; index++) {
    const strip = document.createElement("div");
    strip.className = "genie-strip";
    Object.assign(strip.style, {
      left: `${rect.left}px`, top: `${rect.top + index * sliceHeight}px`,
      width: `${rect.width}px`, height: `${sliceHeight + .7}px`,
    });
    const copy = element.cloneNode(true) as HTMLElement;
    copy.hidden = false;
    copy.removeAttribute("id");
    copy.querySelectorAll("[id]").forEach(node => node.removeAttribute("id"));
    delete copy.dataset.windowMotion;
    delete copy.dataset.windowGlide;
    Object.assign(copy.style, {
      position: "absolute", left: "0", top: `${-index * sliceHeight}px`,
      width: `${rect.width}px`, height: `${rect.height}px`, maxHeight: "none",
      margin: "0", translate: "none", transform: "none", visibility: "visible",
      boxShadow: "none", pointerEvents: "none",
    });
    copy.querySelectorAll("iframe, object, embed, video").forEach(media => {
      const surface = document.createElement("div"); surface.className = "genie-document-surface";
      surface.textContent = media.getAttribute("title") ?? "Document";
      surface.style.height = `${(element.querySelector("iframe, object, embed, video") as HTMLElement | null)?.clientHeight ?? 400}px`;
      media.replaceWith(surface);
    });
    strip.appendChild(copy);
    overlay.appendChild(strip);
    strips.push(strip);
  }
  document.body.appendChild(overlay);
  strips.forEach(strip => {
    const nodes = [strip.firstElementChild!, ...Array.from(strip.firstElementChild!.querySelectorAll<HTMLElement>("*"))];
    scrolls.forEach(scroll => {
      const node = nodes[scroll.index] as HTMLElement;
      if (!node) return;
      node.scrollTop = scroll.top;
      node.scrollLeft = scroll.left;
    });
  });
  const dockX = target.left + target.width / 2;
  const dockY = target.top + target.height / 2;
  const smooth = (value: number) => {
    const t = Math.max(0, Math.min(1, value));
    return t * t * (3 - 2 * t);
  };
  const render = (progress: number) => {
    // The bottom pinches first. The upper edge follows it down into the dock.
    const travel = smooth((progress - .16) / .84);
    const pinch = smooth(progress / .72);
    const row = (v: number) => {
      const bend = smooth(v);
      const narrow = pinch * (.16 + .84 * bend);
      const width = rect.width * (1 - narrow) + target.width * narrow;
      const center = rect.left + rect.width / 2 + (dockX - rect.left - rect.width / 2) * narrow;
      const y = rect.top + rect.height * v;
      return {
        width: width * (1 - travel) + target.width * travel,
        center: center * (1 - travel) + dockX * travel,
        y: y * (1 - travel) + (dockY + (v - .5) * target.height) * travel,
      };
    };
    strips.forEach((strip, index) => {
      const top = row(index / count);
      const bottom = row((index + 1) / count);
      const width = Math.max(top.width, bottom.width);
      const left = top.center - width / 2;
      const shear = (bottom.center - top.center) / sliceHeight;
      const heightScale = (bottom.y - top.y) / sliceHeight;
      const topInset = (width - top.width) / (2 * width) * 100;
      const bottomInset = (width - bottom.width) / (2 * width) * 100;
      strip.style.clipPath = `polygon(${topInset}% 0,${100 - topInset}% 0,${100 - bottomInset}% 100%,${bottomInset}% 100%)`;
      strip.style.transform = `matrix(${width / rect.width},0,${shear},${heightScale},${left - rect.left},${top.y - rect.top - index * sliceHeight})`;
    });
    overlay.style.opacity = String(1 - smooth((progress - .91) / .09));
  };
  render(restoring ? 1 : 0);
  // Hide the real window for the entire transition. Its final visibility is
  // committed before the temporary visual is removed, preventing a flash.
  element.style.visibility = "hidden";
  let raf = 0;
  let started: number | null = null;
  const duration = restoring ? 260 : 300;
  const tick = (now: number) => {
    if (started === null) started = now;
    const t = Math.min(1, (now - started) / duration);
    render(restoring ? 1 - t : t);
    if (t < 1) raf = requestAnimationFrame(tick);
    else onFinish();
  };
  raf = requestAnimationFrame(tick);
  return () => {
    cancelAnimationFrame(raf);
    overlay.remove();
  };
}
